import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LangContext';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error(t('alerts.all_fields')); return; }
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(t('dashboard.welcome') + ' ' + user.first_name + '!');
      if (redirectTo) {
        navigate(redirectTo);
        return;
      }
      if (user.role === 'teacher' || user.role === 'admin') navigate('/dashboard/teacher');
      else navigate('/dashboard/student');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(t('alerts.email_not_verified'));
        return;
      }
      toast.error(err.response?.data?.error || err.response?.data?.detail || t('alerts.invalid_credentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden items-center justify-center p-12">
        <div className="relative z-10 max-w-md text-primary-foreground">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary-foreground/20 flex items-center justify-center">
              <GraduationCap className="h-6 w-6" />
            </div>
            <span className="text-2xl font-bold">{t('brand.name')}</span>
          </div>
          <h2 className="text-3xl font-semibold mb-4 leading-tight">{t('auth.welcome_back')}</h2>
          <p className="text-sm opacity-80 leading-relaxed">{t('auth.welcome_desc')}</p>
          <div className="mt-8 space-y-3">
            {['auth.track', 'auth.access_materials', 'auth.view_schedule'].map((key) => (
              <div key={key} className="flex items-center gap-2 text-sm opacity-80">
                <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                <span>{t(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold mb-2">{t('auth.sign_in')}</h1>
            <p className="text-sm text-muted-foreground">{t('auth.sign_in_desc')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-1.5">{t('auth.email')}</Label>
              <Input type="email" className="rounded-lg" placeholder={t('auth.email')} value={email} onChange={e => setEmail(e.target.value)} data-testid="auth-email-input" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5">{t('auth.password')}</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} className="rounded-lg pr-10" placeholder={t('auth.password')} value={password} onChange={e => setPassword(e.target.value)} data-testid="auth-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-2 text-right text-sm">
                <Link to="/forgot-password" className="text-primary font-medium hover:underline">
                  {t('auth.forgot_password')}
                </Link>
              </div>
            </div>
            <Button type="submit" className="w-full rounded-xl h-11" disabled={loading} data-testid="auth-login-submit-button">
              {loading ? t('auth.signing_in') : t('auth.sign_in')} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <Separator className="my-6" />

          <p className="text-center text-sm text-muted-foreground">
            {t('auth.no_account')}{' '}
            <Link to={redirectTo ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register'} className="text-primary font-medium hover:underline" data-testid="goto-register-link">{t('auth.create_one')}</Link>
          </p>

        </motion.div>
      </div>
    </div>
  );
}
